/**
 * Response shapes for `coaching-analytics`. Every section is scoped by the same
 * cohort filter and returns counts with their denominators, so a percentage can
 * be shown without inventing one.
 */

/** The filter bar's sentinel for "not narrowed". */
export const ALL_FILTER_VALUE = 'all';

/** The filter bar's sentinel for "no value recorded". */
export const NO_DATA_FILTER_VALUE = 'none';

export type AnalyticsFilter = Readonly<{
  institution?: string;
  grade?: string;
  programs?: string;
  enrollment?: string;
  coaches?: string;
}>;

export type CountOfTotal = Readonly<{ count: number; total: number }>;

export type CountWithPercentage = CountOfTotal & Readonly<{ percentage: number }>;

export type BucketCount = Readonly<{ id: string; count: number }>;

export type FilterOptionsResponse = Readonly<{
  institutions: readonly Readonly<{ id: number; name: string }>[];
  grades: readonly Readonly<{ value: string; name: string | null; count: number }>[];
  programs: readonly Readonly<{ value: string; count: number }>[];
  enrollment_types: readonly Readonly<{
    value: string;
    name: string | null;
    level: string | null;
    count: number;
  }>[];
}>;

export type CoachCounts = Readonly<{
  total: number;
  backrs: number;
  institutional: number;
}>;

export type OverviewResponse = Readonly<{
  students: Readonly<{
    total: number;
    by_stage: readonly BucketCount[];
    by_program: readonly BucketCount[];
  }>;
  recently_active: CountWithPercentage;
  /** Institution scope only — a coach's own population has no coach roster. */
  coaches?: CoachCounts;
  completions: Readonly<{
    blueprint: CountWithPercentage;
    are_you_on_track: CountWithPercentage;
    life_readiness: CountWithPercentage;
  }>;
}>;

export type CareerPathFitCategory = Readonly<{
  id: string;
  category_id: number;
  name: string;
  short_name: string | null;
  students: Readonly<{ strong: number; medium: number; low: number }>;
  results: Readonly<{ strong: number; medium: number; low: number }>;
}>;

export type BlueprintResponse = Readonly<{
  students: Readonly<{ total: number }>;
  unique_blueprints: CountWithPercentage;
  entrepreneurship_recommended: CountWithPercentage;
  target_career_tracks: Readonly<{ total: number; tiers: readonly BucketCount[] }>;
  career_path_fit: Readonly<{
    total: number;
    categories: readonly CareerPathFitCategory[];
  }>;
}>;

export type CareerTrack = Readonly<{
  id: string;
  career_track_id: number;
  name: string;
  rank: number;
}>;

/** Counts keyed by career track code. */
export type TrackCounts = Readonly<Record<string, number>>;

export type MeasureBins = Readonly<{
  bin_size: number;
  with_score: number;
  no_score_on_file: number;
  bins: readonly Readonly<{
    min: number;
    max: number;
    total: number;
    tracks: TrackCounts;
  }>[];
}>;

export type RigorBand = Readonly<{
  id: string | number;
  name: string;
  total: number;
  tracks: TrackCounts;
  /** Coursework rigor only: the band the GPA requirement table scores it as. */
  scored_as?: string;
  /** Major rigor only. */
  min_score?: number;
}>;

export type MeasureId = 'gpa' | 'sat' | 'act';

export type AcademicTab = Readonly<{
  total: number;
  /** High school carries all three; post-secondary carries GPA only. */
  measures: Readonly<Partial<Record<MeasureId, MeasureBins>>>;
  /** Post-secondary only: the selectivity of the college named on the assessment. */
  selectivity?: Readonly<{ bands: readonly RigorBand[] }>;
  rigor: Readonly<{
    type: 'coursework' | 'major';
    bands: readonly RigorBand[];
    not_on_file: number;
  }>;
}>;

export type AyotResponse = Readonly<{
  students: Readonly<{ total: number }>;
  assessed: CountWithPercentage;
  tracks: readonly CareerTrack[];
  aspiration_vs_reality: Readonly<{
    total: number;
    excluded: Readonly<{
      no_blueprint: number;
      unrecognized_tier: number;
      unranked_track: number;
    }>;
    statuses: readonly BucketCount[];
    matrix: Readonly<{
      aspirations: readonly Readonly<{
        aspiration: string;
        total: number;
        tracks: TrackCounts;
      }>[];
      track_totals: TrackCounts;
    }>;
  }>;
  academic_profile: Readonly<{
    high_school: AcademicTab;
    post_secondary: AcademicTab;
    unclassified: number;
  }>;
  non_academic_profile: Readonly<{
    total: number;
    excluded: Readonly<{
      no_blueprint: number;
      unrecognized_tier: number;
      no_dimension_scores: number;
    }>;
    statuses: readonly BucketCount[];
    categories: readonly Readonly<{
      id: string;
      category_id: number;
      name: string;
      shortname: string | null;
      counts_toward_verdict: boolean;
      total: number;
      statuses: readonly BucketCount[];
    }>[];
  }>;
}>;

export type ReadinessCategory = Readonly<{
  id: string;
  category_id: number;
  name: string;
  sequence: number | null;
  answers: Readonly<{
    total: number;
    bands: readonly BucketCount[];
  }>;
  coverage: Readonly<{
    questions: number;
    answered: number;
    answerable: number;
    unanswered: number;
  }>;
}>;

export type LifeReadinessResponse = Readonly<{
  students: Readonly<{ total: number }>;
  completion: Readonly<{
    total: number;
    statuses: readonly BucketCount[];
    questions: Readonly<{ visible: number }>;
  }>;
  readiness_by_category: Readonly<{
    students_started: number;
    bands: readonly Readonly<{ id: string; levels: readonly number[] }>[];
    categories: readonly ReadinessCategory[];
  }>;
}>;

export type ActivityBucket = Readonly<{
  index: number;
  start: string;
  posts: number;
  goals: number;
  comments: number;
}>;

export type ActivitySeries = Readonly<{
  buckets: number;
  series: readonly ActivityBucket[];
}>;

export type EngagementResponse = Readonly<{
  students: Readonly<{ total: number }>;
  recently_active: Readonly<{
    total: number;
    buckets: Readonly<{
      last_30: CountOfTotal;
      days_31_60: CountOfTotal;
      days_61_90: CountOfTotal;
      over_90: CountOfTotal;
    }>;
  }>;
  activity: Readonly<{ monthly: ActivitySeries }>;
  networks: readonly Readonly<{
    group_id: number;
    name: string;
    type: string | null;
    count: number;
  }>[];
}>;

export type CoachRow = Readonly<{
  id: number;
  first_name: string | null;
  last_name: string | null;
  type: 'backrs' | 'institutional';
  caseload: number;
  total_caseload: number;
}>;

export type CoachTypeSummary = Readonly<{
  coaches: readonly CoachRow[];
  coach_count: number;
  assignments: number;
  average_per_coach: number;
  students_with_coach: number;
}>;

export type CoachingResponse = Readonly<{
  by_type: Readonly<{
    institutional: CoachTypeSummary;
    backrs: CoachTypeSummary;
  }>;
  coach_counts: CoachCounts;
  coverage: Readonly<{
    students: number;
    with_coach: number;
    without_coach: number;
  }>;
}>;

export type ToolUsageResponse = Readonly<{
  students: Readonly<{
    total: number;
    /** Used at least one tool. Not the sum of the per-tool counts, which overlap. */
    with_any_tool: number;
  }>;
  tools: readonly Readonly<{
    tool: string;
    unique_students: number;
    total_sessions: number;
  }>[];
}>;
